import React, { FC, ComponentProps } from 'react'

import CardTitle from '../../Commons/Elements/CardTitle'

import { EmptySearch } from './EmptySearch'
import { LoadingResults } from './LoadingResults'
import SearchError from './SearchError'
import SearchResults from './SearchResults'
import { NoResults } from './NoResults'

import { FiltersSearch } from '@/checkout-instore-legacy/search/typings'
import { FilterSidebar } from '@/checkout-instore-legacy/components/FilterSidebar'
import { FilterContent } from '@/checkout-instore-legacy/components/FilterSidebar/FilterContent'
import { useFilterSiderbarState } from '@/checkout-instore-legacy/components/FilterSidebar/hook'
import isEmpty from 'lodash/isEmpty'
import FilterMobileBar from '@/checkout-instore-legacy/components/FilterSidebar/FilterMobileBar'

interface ErrorSearch {
  responseMessage: string
}

interface SearchContentProps {
  moreItems: () => void
  retrySearch: () => void
  abortSearch: () => void
  onClickSearchResult: (productId: string, skuId: string) => void
  loadingProducts: boolean
  intervalBegin: number
  results: Product[]
  nextResults: Product[]
  nextLoading: boolean
  term: string
  loading: boolean
  errors: Record<string, string> | ErrorSearch[]
  filters: FiltersSearch
  submitFilter: (attributes: FilterAttributes) => void
  reloadFilters: (term: string, attributes: FilterAttributes) => void
}

type ContentProps = SearchContentProps & ComponentProps<typeof EmptySearch>
type FilterContentProps = React.ComponentProps<typeof FilterContent>
export type FilterAttributes = FilterContentProps['attributes']

const SearchContent: FC<ContentProps> = ({
  term,
  moreItems,
  recentTerms,
  retrySearch,
  updateTerm,
  onClickSearchResult,
  loadingProducts,
  intervalBegin,
  results,
  nextResults,
  nextLoading,
  loading,
  errors,
  abortSearch,
  filters,
  submitFilter,
  reloadFilters,
}) => {
  const {
    attributes,
    clearFilterAttributes,
    handleChangeFilter,
    selectedAttributes,
    modalStatus,
    openModal,
    closeModal,
  } = useFilterSiderbarState(filters)

  const handleSubmitFilters = () => {
    closeModal()
    submitFilter(attributes)
  }

  const handleClearFilters = () => {
    const cleaneredAttributes = clearFilterAttributes()
    submitFilter(cleaneredAttributes)
  }

  const handleReloadFilters = () => {
    reloadFilters(term, attributes)
  }

  if (!term) {
    return <EmptySearch recentTerms={recentTerms} updateTerm={updateTerm} />
  }

  if (errors && errors.length) {
    return <SearchError term={term} errors={errors} retrySearch={retrySearch} />
  }

  const displayFilters =
    filters.status !== 'disabled' &&
    (!isEmpty(results) ||
      !isEmpty(attributes) ||
      !isEmpty(filters.updatedAttributes))

  const displayResults = !loading && !isEmpty(results)

  return (
    <div className="flex w-100 h-100">
      <div
        id="search-content"
        className={
          displayFilters
            ? 'w-70-l w-75-xl mw8 pa7-l pt8-l center-l flex-auto overflow-auto hide-scrollbar'
            : 'w-100 h-100 mw8 center-l pt8-l'
        }
      >
        {displayResults ? (
          <span className="mb7-l dn db-l">
            <CardTitle i18nTextId="search.results" largeType="heading-2" />
          </span>
        ) : null}

        {displayFilters ? (
          <FilterMobileBar
            handleFilterButtonClick={openModal}
            selectedAttributes={selectedAttributes}
            disabled={filters.status === 'error'}
          />
        ) : null}
        {loading ? (
          <LoadingResults term={term} abortSearch={abortSearch} />
        ) : isEmpty(results) ? (
          <NoResults term={term} hasFilters={!isEmpty(attributes)} />
        ) : (
          <SearchResults
            hasNext={nextResults.length > 0}
            onClickSearchResult={onClickSearchResult}
            loading={loading}
            nextLoading={nextLoading}
            errors={errors}
            retrySearch={retrySearch}
            results={results}
            handleMoreItems={moreItems}
            loadingProducts={loadingProducts}
            intervalBegin={intervalBegin}
          />
        )}
      </div>
      {displayFilters ? (
        <FilterSidebar
          term={term}
          status={filters.status}
          openModal={modalStatus}
          submitFilters={handleSubmitFilters}
          clearFilters={handleClearFilters}
          reloadFilters={handleReloadFilters}
          closeModal={closeModal}
        >
          <FilterContent
            attributes={attributes}
            handleChangeFilter={handleChangeFilter}
          />
        </FilterSidebar>
      ) : null}
    </div>
  )
}

export default SearchContent
